import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Copy, Gift, Crown } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../lib/api";
import { spotifyTrack, getUtm } from "../lib/tracking";

const NicknamePrompt = ({ joined }) => {
  const [nick, setNick] = useState("");
  const [saved, setSaved] = useState(joined.nickname || null);
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/leaderboard/nickname`, {
        ref_code: joined.ref_code,
        nickname: nick,
      });
      setSaved(data.nickname);
      toast.success("Crowned — the leaderboard now shows your name.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save that name. Try another.");
    } finally {
      setSaving(false);
    }
  };

  if (saved)
    return (
      <p data-testid="nickname-crowned" className="mt-4 flex items-center gap-2 rounded-2xl bg-amber-500/10 p-4 text-xs font-bold text-amber-600">
        <Crown className="h-4 w-4 shrink-0" /> Reigning on the leaderboard as {saved}
      </p>
    );

  return (
    <form onSubmit={save} data-testid="nickname-form" className="mt-4 rounded-2xl bg-amber-500/10 p-4">
      <p className="flex items-center gap-2 text-xs font-bold text-amber-600">
        <Crown className="h-4 w-4 shrink-0" /> You&apos;re the #1 referrer — claim your leaderboard name
      </p>
      <div className="mt-3 flex gap-2">
        <input
          required
          minLength={2}
          maxLength={24}
          value={nick}
          onChange={(e) => setNick(e.target.value)}
          placeholder="e.g. Queue Champion"
          data-testid="nickname-input"
          className="h-11 flex-1 rounded-full border border-amber-500/30 bg-white px-4 font-manrope text-xs font-semibold text-forest outline-none placeholder:text-forest/40 focus:ring-2 focus:ring-amber-500"
        />
        <button
          type="submit"
          disabled={saving}
          data-testid="nickname-save"
          className="inline-flex h-11 items-center rounded-full bg-forest px-5 font-manrope text-xs font-bold text-bone transition-colors hover:bg-fresh disabled:opacity-60"
        >
          {saving ? "Saving…" : "Claim"}
        </button>
      </div>
    </form>
  );
};

const ReferralPanel = ({ joined }) => {
  const referralLink = `${window.location.origin}/?ref=${joined.ref_code}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      toast.success("Referral link copied — share it anywhere.");
    } catch {
      toast.error("Couldn't copy — select the link and copy manually.");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      data-testid="referral-panel"
      className="mt-10 max-w-lg rounded-3xl border border-fresh/30 bg-white p-6"
    >
      <p className="font-syne text-xl font-extrabold tracking-tight text-forest sm:text-2xl">
        You&apos;re <span className="text-fresh" data-testid="referral-position">#{joined.position}</span> in line.
      </p>
      <p className="mt-2 text-sm leading-relaxed text-forest/60">
        Skip ahead — every friend who joins with your link bumps you up the queue.
      </p>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <input
          readOnly
          value={referralLink}
          data-testid="referral-link"
          onFocus={(e) => e.target.select()}
          className="h-12 flex-1 rounded-full border border-forest/15 bg-mist px-5 font-manrope text-xs font-semibold text-forest outline-none"
        />
        <button
          type="button"
          onClick={copyLink}
          data-testid="referral-copy"
          className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-fresh px-6 font-manrope text-sm font-bold text-white transition-colors duration-300 hover:bg-forest"
        >
          <Copy className="h-4 w-4" /> Copy
        </button>
      </div>
      <div data-testid="referral-milestones" className="mt-4 flex items-start gap-2.5 rounded-2xl bg-fresh/10 p-4">
        <Gift className="mt-0.5 h-4 w-4 shrink-0 text-fresh" />
        <p className="text-xs font-semibold leading-relaxed text-forest/70">
          <span className="font-bold text-forest">
            {joined.referrals || 0} invite{(joined.referrals || 0) === 1 ? "" : "s"} so far.
          </span>{" "}
          Hit <span className="font-bold text-fresh">5 invites</span> for a free month of delivery at
          launch — <span className="font-bold text-fresh">10</span> gets you three months, on us.
        </p>
      </div>
      {joined.position === 1 && (joined.referrals || 0) > 0 && <NicknamePrompt joined={joined} />}
    </motion.div>
  );
};

export const LeadCapture = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(null);

  const submitLead = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const ref = new URLSearchParams(window.location.search).get("ref");
      const { data } = await axios.post(`${API}/leads`, { email, referred_by: ref || null, ...getUtm() });
      setJoined(data);
      spotifyTrack("lead", { type: "waitlist", category: "signup" });
      toast.success(
        data.already_joined
          ? "You're already on the list — here's your spot."
          : "You're on the list — the app link is on its way."
      );
      setEmail("");
    } catch {
      toast.error("Something went wrong. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  if (joined) return <ReferralPanel joined={joined} />;

  return (
    <motion.form
      onSubmit={submitLead}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.9 }}
      className="mt-10 flex max-w-lg flex-col gap-3 sm:flex-row"
    >
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        data-testid="email-lead-input"
        className="h-14 flex-1 rounded-full border border-forest/15 bg-white px-6 font-manrope text-sm text-forest outline-none transition-shadow duration-300 placeholder:text-forest/40 focus:ring-2 focus:ring-fresh"
      />
      <motion.button
        type="submit"
        disabled={loading}
        data-testid="email-lead-submit"
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        className="group inline-flex h-14 items-center justify-center gap-2 rounded-full bg-fresh px-8 font-manrope text-sm font-bold text-white transition-colors duration-300 hover:bg-forest disabled:opacity-60"
      >
        {loading ? "Sending..." : "Download the Freshly App"}
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </motion.button>
    </motion.form>
  );
};
